import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, ChevronDown, Sparkles, Check, Search, X, ArrowLeft, ArrowRight, RefreshCw, Camera } from 'lucide-react';
import { InteriorMenuItem } from '../../types/interiorConfig';
import { useTheme } from '../styles/ThemeContext';
import { useSelections } from './SelectionsContext';
import { DesignMode } from './DesignModeSelector';
// Items that have options configured for demo
const DEMO_ITEMS = new Set(['refrigerator', 'range-oven', 'dishwasher', 'microwave', 'countertop', 'refrigerators', 'refrigerators-lots', 'kitchen-packages']);
export interface CameraView {
  id: string;
  name: string;
  thumbnail: string;
}
interface InteriorSidebarProps {
  menuData: InteriorMenuItem[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onSelectItem: (item: InteriorMenuItem) => void;
  selectedItemId?: string;
  designMode: DesignMode;
  // Step-by-step props
  isStepByStep?: boolean;
  stepIndex?: number;
  totalSteps?: number;
  currentStepItem?: InteriorMenuItem | null;
  currentStepCategory?: string;
  onNextStep?: () => void;
  onPrevStep?: () => void;
  onSwitchView?: () => void;
  // Camera views
  cameraViews?: CameraView[];
  activeCameraId?: string;
  onSelectCamera?: (id: string) => void;
  // Internal toggle for camera view count
  cameraViewCount?: 2 | 3 | 4;
  onCameraViewCountChange?: (count: 2 | 3 | 4) => void;
}
export function InteriorSidebar({
  menuData,
  isCollapsed,
  onToggleCollapse,
  onSelectItem,
  selectedItemId,
  designMode,
  isStepByStep,
  stepIndex = 0,
  totalSteps = 0,
  currentStepItem,
  currentStepCategory,
  onNextStep,
  onPrevStep,
  onSwitchView,
  cameraViews = [],
  activeCameraId,
  onSelectCamera,
  cameraViewCount,
  onCameraViewCountChange
}: InteriorSidebarProps) {
  const {
    isDark
  } = useTheme();
  const {
    selections
  } = useSelections();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['main-options', 'kitchen']));
  const [searchQuery, setSearchQuery] = useState('');
  const [carouselIndex, setCarouselIndex] = useState(0);
  // Reset carousel index when camera views change
  useEffect(() => {
    setCarouselIndex(0);
  }, [cameraViews.length]);
  // Auto-expand categories when menu data changes
  useEffect(() => {
    const newExpanded = new Set(expandedIds);
    menuData.forEach(item => {
      if (item.type === 'category') {
        newExpanded.add(item.id);
      }
    });
    setExpandedIds(newExpanded);
  }, [menuData]);
  // Auto-expand sections that contain search matches
  useEffect(() => {
    if (!searchQuery.trim()) return;
    const query = searchQuery.toLowerCase();
    const idsToExpand = new Set(expandedIds);
    const findMatches = (items: InteriorMenuItem[]) => {
      items.forEach(item => {
        if (item.children) {
          const hasMatchingChild = item.children.some(child => {
            if (child.name.toLowerCase().includes(query)) return true;
            if (child.children) return child.children.some(gc => gc.name.toLowerCase().includes(query));
            return false;
          });
          if (hasMatchingChild) {
            idsToExpand.add(item.id);
          }
          // Also check grandchildren — expand intermediate parents
          item.children.forEach(child => {
            if (child.children) {
              const hasDeepMatch = child.children.some(gc => gc.name.toLowerCase().includes(query));
              if (hasDeepMatch) {
                idsToExpand.add(child.id);
              }
            }
          });
          findMatches(item.children);
        }
      });
    };
    findMatches(menuData);
    setExpandedIds(idsToExpand);
  }, [searchQuery, menuData]);
  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };
  // Count completed items in a category
  const getCompletedCount = (item: InteriorMenuItem): {
    completed: number;
    total: number;
  } => {
    let completed = 0;
    let total = 0;
    const count = (items: InteriorMenuItem[]) => {
      items.forEach(child => {
        if (child.type === 'item' && !child.children) {
          total++;
          if (selections.some(s => s.itemName === child.name)) {
            completed++;
          }
        }
        if (child.children) count(child.children);
      });
    };
    if (item.children) count(item.children);
    return {
      completed,
      total
    };
  };
  // Search aliases for demo purposes
  const SEARCH_ALIASES: Record<string, string[]> = {
    refrigerator: ['fridge', 'fridges'],
    refrigerators: ['fridge', 'fridges'],
    'range oven': ['stove', 'oven', 'range'],
    dishwasher: ['dishes'],
    microwave: ['micro'],
    countertop: ['counter', 'counters'],
    'kitchen packages': ['packages', 'bundle', 'bundles']
  };
  // Filter items by search
  const matchesSearch = (item: InteriorMenuItem): boolean => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const name = item.name.toLowerCase();
    // Direct name match
    if (name.includes(query)) return true;
    // Check aliases — does the query match any alias for this item?
    for (const [itemKey, aliases] of Object.entries(SEARCH_ALIASES)) {
      if (name === itemKey && aliases.some(alias => alias.includes(query) || query.includes(alias))) {
        return true;
      }
    }
    if (item.children) return item.children.some(child => matchesSearch(child));
    return false;
  };
  const renderMenuItem = (item: InteriorMenuItem, level: number = 0) => {
    if (!matchesSearch(item)) return null;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedIds.has(item.id);
    const isSelected = selectedItemId === item.id;
    const isCategory = item.type === 'category';
    const isDemoItem = DEMO_ITEMS.has(item.id);
    const isCompleted = selections.some(s => s.itemName === item.name);
    // Category headers — card-style like Amenities
    if (isCategory) {
      const {
        completed,
        total
      } = getCompletedCount(item);
      const hasCompletions = completed > 0;
      return <div key={item.id} className="space-y-1" data-id="element-786">
          <button onClick={() => toggleExpand(item.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors text-left ${isDark ? 'bg-slate-800/30 hover:bg-slate-800/50 border-white/5' : 'bg-white/40 hover:bg-white/60 border-white/40'}`} data-id="element-787">
            {/* Category icon badge */}
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold border ${hasCompletions ? isDark ? 'bg-cyan-500/20 border-cyan-500/35 text-cyan-400' : 'bg-teal-500/15 border-teal-400/30 text-teal-700' : isDark ? 'bg-white/5 border-white/10 text-white/40' : 'bg-gray-100 border-gray-200 text-gray-400'}`} data-id="element-788">
              {item.name.charAt(0)}
            </div>

            <div className="flex-1 min-w-0" data-id="element-789">
              <div className="flex items-center gap-2" data-id="element-790">
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-791">
                  {item.name}
                </span>
                {total > 0 && <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-792">
                    ({completed}/{total})
                  </span>}
              </div>
            </div>

            {isExpanded ? <ChevronDown className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} data-id="element-793" /> : <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} data-id="element-794" />}
          </button>

          <AnimatePresence data-id="element-795">
            {isExpanded && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden" data-id="element-796">
                <div className="pl-2 space-y-0.5 py-1" data-id="element-797">
                  {item.children!.map(child => renderMenuItem(child, level + 1))}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>;
    }
    // Subcategory with children (e.g., "Kitchen" under "Main Options")
    if (hasChildren) {
      const {
        completed,
        total
      } = getCompletedCount(item);
      return <div key={item.id} className="space-y-0.5" data-id="element-798">
          <button onClick={() => {
          toggleExpand(item.id);
          onSelectItem(item);
        }} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors text-left group ${isSelected ? isDark ? 'bg-cyan-500/15 text-white' : 'bg-teal-500/10 text-gray-900' : isDark ? 'hover:bg-slate-800/50 text-white/70 group-hover:text-white' : 'hover:bg-white/60 text-gray-600 group-hover:text-gray-900'}`} data-id="element-799">
            {isCompleted || completed > 0 ? <div className={`flex items-center justify-center w-5 h-5 rounded-md ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-500/15'}`} data-id="element-800">
                <Check className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} data-id="element-801" />
              </div> : <div className={`w-5 h-5 rounded-md flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`} data-id="element-802">
                <div className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} data-id="element-803" />
              </div>}

            <span className="flex-1 text-sm font-medium flex items-center gap-1.5" data-id="element-804">
              {item.name}
              {total > 0 && <span className={`text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-805">
                  {completed}/{total}
                </span>}
            </span>

            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 flex-shrink-0 ${isDark ? 'text-white/30' : 'text-gray-400'} ${isExpanded ? '' : '-rotate-90'}`} data-id="element-806" />
          </button>

          <AnimatePresence data-id="element-807">
            {isExpanded && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden" data-id="element-808">
                <div className="pl-3 space-y-0.5 py-0.5" data-id="element-809">
                  {item.children!.map(child => renderMenuItem(child, level + 1))}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>;
    }
    // Leaf items — clean row like Amenities child items
    return <button key={item.id} onClick={() => onSelectItem(item)} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors text-left group ${isSelected ? isDark ? 'bg-cyan-500/15' : 'bg-teal-500/10' : isDark ? 'hover:bg-slate-800/50' : 'hover:bg-white/60'}`} data-id="element-810">
        {isCompleted ? <div className={`flex items-center justify-center w-5 h-5 rounded-md ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-500/15'}`} data-id="element-811">
            <Check className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} data-id="element-812" />
          </div> : <div className={`w-5 h-5 rounded-md flex items-center justify-center ${isSelected ? isDark ? 'bg-cyan-500/20' : 'bg-teal-500/15' : isDark ? 'bg-white/5' : 'bg-gray-50'}`} data-id="element-813">
            <div className={`w-1.5 h-1.5 rounded-full transition-colors ${isSelected ? isDark ? 'bg-cyan-400' : 'bg-teal-600' : isDark ? 'bg-white/20 group-hover:bg-white/40' : 'bg-gray-300 group-hover:bg-gray-500'}`} data-id="element-814" />
          </div>}

        <span className={`flex-1 text-sm transition-colors ${isSelected ? isDark ? 'text-white font-medium' : 'text-gray-900 font-medium' : isCompleted ? isDark ? 'text-white/90' : 'text-gray-800' : isDark ? 'text-slate-300 group-hover:text-white' : 'text-gray-600 group-hover:text-gray-900'}`} data-id="element-815">
          {item.name}
        </span>

        {isDemoItem && !isCompleted && <Sparkles className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-816" />}
      </button>;
  };
  const modeLabel = designMode === 'exterior' ? 'Exterior' : 'Interior';
  const modeSubtitle = designMode === 'exterior' ? 'Customize facade & finishes' : 'Customize finishes & fixtures';
  return <>
      {/* Sidebar */}
      <motion.div initial={false} animate={{
      width: isCollapsed ? 0 : 260,
      x: isCollapsed ? -260 : 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className={`absolute top-0 left-[100px] bottom-0 z-40 backdrop-blur-xl shadow-2xl overflow-hidden flex flex-col transition-colors duration-300 ${isDark ? 'bg-slate-900/70 border-r border-white/10' : 'bg-white/70 border-r border-white/40'}`} data-id="element-817">
        {/* Header — matches Amenities style */}
        <div className={`p-5 transition-colors duration-300 ${isDark ? 'border-b border-white/10' : 'border-b border-white/40'}`} data-id="element-818">
          <h2 className={`text-lg font-light mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-819">
            Customize{' '}
            <span className={`font-bold ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-820">
              {modeLabel}
            </span>
          </h2>
          <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`} data-id="element-821">
            {modeSubtitle}
          </p>

          {/* Search */}
          <div className="relative mt-3" data-id="element-822">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} data-id="element-823" />
            <input type="text" placeholder="Search options..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-9 pr-8 py-1.5 border rounded-lg text-xs focus:outline-none transition-colors ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-slate-500 focus:border-cyan-500/50' : 'bg-white/50 border-white/40 text-gray-900 placeholder-gray-400 focus:border-teal-500/50'}`} data-id="element-824" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className={`absolute right-2.5 top-1/2 -translate-y-1/2 transition-colors ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`} data-id="element-825">
                <X className="w-3.5 h-3.5" data-id="element-826" />
              </button>}
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={`${isStepByStep ? 'flex-1 min-h-0' : 'flex-1'} overflow-y-auto custom-scrollbar relative`} data-id="element-827">
          <div className={`p-3 space-y-1.5`} data-id="element-828">
            {menuData.map(item => renderMenuItem(item))}
          </div>
        </div>

        {/* Step-by-Step Pagination Panel (Anchored to bottom of sidebar) */}
        <AnimatePresence data-id="element-829">
          {isStepByStep && <motion.div initial={{
          y: 100,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 100,
          opacity: 0
        }} className={`flex-1 min-h-0 flex flex-col p-5 border-t backdrop-blur-xl z-10 ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} data-id="element-830">
              <div className="flex flex-col justify-between h-full gap-4" data-id="element-831">
                {/* Step counter + percentage */}
                <div data-id="element-832">
                  <div className="flex items-center justify-between mb-4" data-id="element-833">
                    <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-834">
                      Step {stepIndex + 1} of {totalSteps}
                    </span>
                    <span className={`text-xs font-medium ${isDark ? 'text-white/50' : 'text-gray-400'}`} data-id="element-835">
                      {Math.round((stepIndex + 1) / totalSteps * 100)}%
                    </span>
                  </div>

                  {/* Progress Bar — thicker */}
                  <div className={`h-1.5 w-full rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} data-id="element-836">
                    <motion.div className={`h-full rounded-full ${isDark ? 'bg-cyan-500' : 'bg-teal-600'}`} initial={false} animate={{
                  width: `${(stepIndex + 1) / totalSteps * 100}%`
                }} transition={{
                  duration: 0.3,
                  ease: [0.22, 1, 0.36, 1]
                }} data-id="element-837" />
                  </div>
                </div>

                {/* Current step info + nav arrows */}
                <div className="flex items-center justify-between gap-3" data-id="element-838">
                  {stepIndex > 0 ? <button onClick={onPrevStep} className={`p-2.5 rounded-xl border transition-colors ${isDark ? 'border-white/10 hover:bg-white/5 text-white' : 'border-gray-200 hover:bg-gray-50 text-gray-900'}`} data-id="element-839">
                      <ArrowLeft className="w-4 h-4" data-id="element-840" />
                    </button> : <div className="w-[44px]" data-id="element-841" />}

                  <div className="flex-1 text-center min-w-0" data-id="element-842">
                    <div className={`text-[10px] uppercase tracking-wider mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-843">
                      {currentStepCategory}
                    </div>
                    <div className={`text-sm font-bold text-center leading-snug px-1 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-844">
                      {currentStepItem?.name || 'Select Option'}
                    </div>
                  </div>

                  {stepIndex < totalSteps - 1 ? <button onClick={onNextStep} className={`p-2.5 rounded-xl border transition-colors ${isDark ? 'border-white/10 hover:bg-white/5 text-white' : 'border-gray-200 hover:bg-gray-50 text-gray-900'}`} data-id="element-845">
                      <ArrowRight className="w-4 h-4" data-id="element-846" />
                    </button> : <div className="w-[44px]" data-id="element-847" />}
                </div>

                {/* Camera Views Carousel — replaces Switch View when views available */}
                {cameraViews.length > 1 && onSelectCamera ? <div className="flex flex-col gap-2" data-id="element-848">
                    {/* Label + view count toggle */}
                    <div className="flex items-center justify-between" data-id="element-849">
                      <div className="flex items-center gap-1.5" data-id="element-850">
                        <Camera className={`w-3 h-3 ${isDark ? 'text-cyan-400/70' : 'text-teal-600/70'}`} data-id="element-851" />
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-852">
                          Cameras
                        </span>
                      </div>

                      {/* View count toggle (internal) */}
                      {onCameraViewCountChange ? <div className={`flex items-center rounded-full p-0.5 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-100 border border-gray-200'}`} data-id="element-853">
                          {([2, 3, 4] as const).map(n => <button key={n} onClick={() => onCameraViewCountChange(n)} className={`w-5 h-5 rounded-full text-[9px] font-bold transition-all flex items-center justify-center ${cameraViewCount === n ? isDark ? 'bg-cyan-500 text-white' : 'bg-teal-600 text-white' : isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-400 hover:text-gray-700'}`} data-id="element-854">
                              {n}
                            </button>)}
                        </div> : <span className={`text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-855">
                          {carouselIndex + 1}/{cameraViews.length}
                        </span>}
                    </div>

                    {/* Carousel */}
                    <div className="relative" data-id="element-856">
                      {/* Thumbnail */}
                      <button onClick={() => {
                  const view = cameraViews[carouselIndex];
                  if (view.id !== activeCameraId) {
                    onSelectCamera(view.id);
                  }
                }} className={`w-full aspect-[16/9] rounded-lg overflow-hidden border-2 transition-all relative group ${cameraViews[carouselIndex]?.id === activeCameraId ? isDark ? 'border-cyan-500 shadow-[0_0_12px_rgba(6,182,212,0.3)]' : 'border-teal-500 shadow-[0_0_12px_rgba(13,148,136,0.2)]' : isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-857">
                        <AnimatePresence mode="wait" data-id="element-858">
                          <motion.img key={cameraViews[carouselIndex]?.id} src={cameraViews[carouselIndex]?.thumbnail} alt={cameraViews[carouselIndex]?.name} className="w-full h-full object-cover" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} data-id="element-859" />
                        </AnimatePresence>

                        {/* Active badge */}
                        {cameraViews[carouselIndex]?.id === activeCameraId && <div className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${isDark ? 'bg-cyan-500 text-white' : 'bg-teal-600 text-white'}`} data-id="element-860">
                            Active
                          </div>}

                        {/* Hover overlay for non-active */}
                        {cameraViews[carouselIndex]?.id !== activeCameraId && <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center" data-id="element-861">
                            <span className="text-white text-xs font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" data-id="element-862">
                              Switch
                            </span>
                          </div>}

                        {/* View name */}
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5" data-id="element-863">
                          <span className="text-[10px] font-medium text-white/90" data-id="element-864">
                            {cameraViews[carouselIndex]?.name}
                          </span>
                        </div>
                      </button>

                      {/* Prev/Next arrows */}
                      {cameraViews.length > 1 && <>
                          <button onClick={() => setCarouselIndex(prev => (prev - 1 + cameraViews.length) % cameraViews.length)} className={`absolute left-1 top-1/2 -translate-y-1/2 p-1 rounded-full backdrop-blur-sm transition-all ${isDark ? 'bg-black/50 text-white/80 hover:bg-black/70' : 'bg-white/70 text-gray-700 hover:bg-white/90'}`} data-id="element-865">
                            <ChevronLeft className="w-3.5 h-3.5" data-id="element-866" />
                          </button>
                          <button onClick={() => setCarouselIndex(prev => (prev + 1) % cameraViews.length)} className={`absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full backdrop-blur-sm transition-all ${isDark ? 'bg-black/50 text-white/80 hover:bg-black/70' : 'bg-white/70 text-gray-700 hover:bg-white/90'}`} data-id="element-867">
                            <ChevronRight className="w-3.5 h-3.5" data-id="element-868" />
                          </button>
                        </>}
                    </div>

                    {/* Small thumbnail strip — replaces dot indicators */}
                    <div className="flex items-center gap-1.5" data-id="element-869">
                      {cameraViews.map((view, i) => <button key={view.id} onClick={() => {
                  setCarouselIndex(i);
                  if (view.id !== activeCameraId && onSelectCamera) {
                    onSelectCamera(view.id);
                  }
                }} className={`relative flex-1 aspect-[16/10] rounded overflow-hidden border-[1.5px] transition-all ${i === carouselIndex ? view.id === activeCameraId ? isDark ? 'border-cyan-400 shadow-[0_0_8px_rgba(6,182,212,0.25)]' : 'border-teal-500 shadow-[0_0_8px_rgba(13,148,136,0.15)]' : isDark ? 'border-white/50' : 'border-gray-500' : view.id === activeCameraId ? isDark ? 'border-cyan-400/40' : 'border-teal-500/40' : isDark ? 'border-white/10 hover:border-white/25' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-870">
                          <img src={view.thumbnail} alt={view.name} className={`w-full h-full object-cover transition-opacity ${i === carouselIndex ? 'opacity-100' : 'opacity-60 hover:opacity-90'}`} data-id="element-871" />
                          {/* Active dot indicator on the small thumb */}
                          {view.id === activeCameraId && <div className={`absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-872" />}
                        </button>)}
                    </div>
                  </div> : onSwitchView /* Fallback: generic Switch View button */ ? <button onClick={onSwitchView} className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border transition-all group ${isDark ? 'border-white/10 hover:bg-white/5 text-white/70 hover:text-white' : 'border-gray-200 hover:bg-gray-50 text-gray-600 hover:text-gray-900'}`} data-id="element-873">
                    <RefreshCw className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" data-id="element-874" />
                    <span className="text-xs font-bold uppercase tracking-wider" data-id="element-875">
                      Switch View
                    </span>
                  </button> : null}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Footer */}
        {!isStepByStep && <div className={`p-4 transition-colors duration-300 relative z-20 ${isDark ? 'border-t border-white/5' : 'border-t border-white/40'}`} data-id="element-876" />}
      </motion.div>

      {/* Collapse Toggle */}
      <motion.button animate={{
      left: isCollapsed ? 100 : 360
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} onClick={onToggleCollapse} className={`absolute top-6 z-50 flex items-center gap-2 rounded-r-md border border-l-0 shadow-xl transition-colors ${isCollapsed ? 'px-3 py-2' : 'p-1.5'} ${isDark ? 'bg-slate-900/70 text-white/50 hover:text-white border-white/10' : 'bg-white/70 text-gray-500 hover:text-gray-900 border-white/40'}`} data-id="element-877">
        {isCollapsed ? <>
            <ChevronRight className="w-4 h-4" data-id="element-878" />
            <span className={`text-[11px] font-bold uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-879">
              Menu
            </span>
          </> : <ChevronLeft className="w-4 h-4" data-id="element-880" />}
      </motion.button>
    </>;
}